import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { dateToMonthAndYear } from '../../../../services/dateFormatter';
import { LEVELS } from '../../../atoms/RelationshipStrengthIndicator';

const getNodeLabelAndAvatar = (node) => {
  if (!node) return { name: '', avatarUrl: '', currentJobTitle: '', currentCompany: '', emails: [] };
  if (node.kind === 'user') {
    const attrs = node.user?.data?.attributes || {};
    return {
      name: attrs.name || '',
      avatarUrl: attrs.avatarUrl || '',
      currentJobTitle: attrs.linkedinTagline || attrs.currentJobTitle || '',
      currentCompany: attrs.currentCompany || attrs.companyName || '',
      emails: Array.isArray(attrs.emails) ? attrs.emails : [],
    };
  }
  const attrs = node.contact?.data?.attributes || {};
  return {
    name: attrs.fullName || '',
    avatarUrl: attrs.profilePicUrl || '',
    currentJobTitle: attrs.linkedinTagline || attrs.currentJobTitle || '',
    currentCompany: attrs.currentCompany || attrs.companyName || '',
    emails: Array.isArray(attrs.emails) ? attrs.emails : [],
  };
};

const formatOverlapInfo = (node) => {
  const fromFormatted = node.overlapFrom ? dateToMonthAndYear(node.overlapFrom) : '';
  const toFormatted = node.overlapTo ? dateToMonthAndYear(node.overlapTo) : '';
  const org = node.organizationName ? `at ${node.organizationName} ` : '';

  return node.bothCurrentlyAtCompany
    ? `Work overlap: currently ${org}(since ${fromFormatted})`
    : `Work overlap: ${org}(${fromFormatted} – ${toFormatted})`;
};

const getSignalProperties = (type, node) => {
  const properties = {
    linkedin: type === 'linkedin_connection',
    google: type === 'google_connection',
    workOverlap: type === 'work_overlap',
  };

  const extraInfo = [];
  if (type === 'linkedin_connection') {
    extraInfo.push(
      `LinkedIn: 1st-degree connection${
        node.connectedAt && node.connectedAt !== 0 ? ` (since ${dateToMonthAndYear(node.connectedAt)})` : ''
      }`,
    );
  }
  if (type === 'google_connection') {
    extraInfo.push('Interaction: emails or meetings detected');
  }
  if (type === 'work_overlap') {
    extraInfo.push(formatOverlapInfo(node));
  }

  return { ...properties, extraInfo };
};

// Work-overlap and LinkedIn criteria already state their timing in the label
// ("(since 2024)", "since <year>"), so their per-bridge touch is dropped to
// avoid repeating it; interaction bridges (email/meeting) keep theirs.
const LABEL_STATES_OWN_TIMING = new Set(['work_overlap', 'linkedin']);

// Pair each criterion label with its per-bridge last touch from the BE
// explanation evidence, matched by label. Falls back to null when there is no
// evidence (signal-derived criteria) or the bridge states its own timing.
const criteriaWithLastTouch = (labels, evidence = []) => {
  const touchByLabel = new Map(
    evidence
      .filter((entry) => !LABEL_STATES_OWN_TIMING.has(entry.category))
      .map((entry) => [entry.label, entry.lastTouch]),
  );
  return labels.map((label) => ({ label, lastTouch: touchByLabel.get(label) ?? null }));
};

export function useIntroPathCard({ item }) {
  const currentUserId = useSelector((s) => s.user?.id);
  const pathNodes = useMemo(() => item?.path || [], [item?.path]);
  // The categorical verdict is a per-path passthrough (sibling to type/signals).
  // The whole relationship-strength contract lives here so later BE drift is a
  // one-file change.
  const level = item?.level;
  // Per-leg verdict + evidence the BE now pre-computes (GET-433): `primary` =
  // first leg, `secondary` = last leg. Absent on older read paths, where we fall
  // back to the single `level` + signal-derived criteria below.
  const perLegStrength = item?.relationshipStrength;
  const perLegCriteria = item?.metCriteria;
  // Per-leg explanation the BE pre-computes (GET-784): per-bridge evidence, each
  // carrying its own `last_touch`, surfaced inline on the matching criterion.
  // Same primary/secondary leg attribution as strength + criteria.
  const perLegExplanation = item?.explanation;

  return useMemo(() => {
    const nodesForRender = [];

    pathNodes.forEach((node, index) => {
      if (node.kind === 'user' || node.kind === 'contact') {
        const info = getNodeLabelAndAvatar(node);
        const key = `${node.kind}-${node.id}-${index}`;
        const isNodeYou =
          node.kind === 'user' &&
          (String(node.id) === String(currentUserId) || String(node?.user?.data?.id) === String(currentUserId));
        const labelName = isNodeYou ? 'You' : info.name || `${node.kind}-${node.id}`;
        const avatarName = info.name || `${node.kind}-${node.id}`;

        nodesForRender.push({
          id: node.id,
          key,
          isYou: isNodeYou,
          labelName,
          avatarName,
          avatarUrl: info.avatarUrl,
          kind: node.kind,
          currentJobTitle: info.currentJobTitle,
          currentCompany: info.currentCompany,
          emails: info.emails,
        });
      } else {
        const lastRenderedNode = nodesForRender[nodesForRender.length - 1];
        const signalProps = getSignalProperties(node.type, node);

        if (lastRenderedNode?.kind === 'signal') {
          Object.assign(lastRenderedNode, {
            linkedin: lastRenderedNode.linkedin || signalProps.linkedin,
            google: lastRenderedNode.google || signalProps.google,
            workOverlap: lastRenderedNode.workOverlap || signalProps.workOverlap,
          });
          lastRenderedNode.extraInfo.push(...signalProps.extraInfo);
        } else {
          nodesForRender.push({
            kind: 'signal',
            key: `${lastRenderedNode?.kind}-${lastRenderedNode?.id}-${node.type}`,
            ...signalProps,
          });
        }
      }
    });

    // The strong/medium/weak indicator comes from `level`; the checkmark rows
    // (met-criteria) are derived FE-side from the signal evidence already in
    // the response — no dedicated BE field. A missing/unrecognised verdict
    // resolves to `unknown` (the BE emits `unknown` for zero-signal paths
    // anyway), so a flag-on collection shows the enriched card even before the
    // read path scores it, rather than falling back to a blank legacy card.
    const personNodesForLegs = nodesForRender.filter((node) => node.kind !== 'signal');

    // Per-leg levels keyed by connector position: BE `relationship_strength.
    // {primary,secondary}` (primary = first leg, secondary = last leg). Assigned
    // by index (not pushed) so a primary-less payload never shifts secondary onto
    // the first connector; the card fills any gap with the overall level. Mirrors
    // the met-criteria leg attribution below (secondary needs >= 3 person nodes).
    const legLevels = [];
    if (perLegStrength?.primary) legLevels[0] = perLegStrength.primary;
    if (perLegStrength?.secondary && personNodesForLegs.length >= 3) {
      legLevels[personNodesForLegs.length - 2] = perLegStrength.secondary;
    }

    // Per-leg met-criteria, attributed to the leg (person pair) it covers so a
    // multi-hop path never reads as a duplicated flat list. Prefer the BE's
    // `met_criteria.{primary,secondary}` (BE owns the taxonomy); otherwise derive
    // from the signal evidence already in the path. `metCriteria` stays flat for
    // the title gate + feedback payload.
    // Empty arrays count as "no BE evidence" so they don't suppress the
    // signal-derived fallback below (an `[]` is still truthy).
    const bePrimary =
      Array.isArray(perLegCriteria?.primary) && perLegCriteria.primary.length ? perLegCriteria.primary : null;
    const beSecondary =
      Array.isArray(perLegCriteria?.secondary) && perLegCriteria.secondary.length ? perLegCriteria.secondary : null;
    // Each criterion carries its own per-bridge last touch (interaction bridges
    // only); work-overlap/linkedin state their timing in the label already.
    const metCriteriaGroups = [];
    if (bePrimary || beSecondary) {
      if (bePrimary) {
        metCriteriaGroups.push({
          from: personNodesForLegs[0]?.avatarName || '',
          to: personNodesForLegs[1]?.avatarName || '',
          criteria: criteriaWithLastTouch(bePrimary, perLegExplanation?.primary?.evidence),
        });
      }
      if (beSecondary && personNodesForLegs.length >= 3) {
        metCriteriaGroups.push({
          from: personNodesForLegs[personNodesForLegs.length - 2]?.avatarName || '',
          to: personNodesForLegs[personNodesForLegs.length - 1]?.avatarName || '',
          criteria: criteriaWithLastTouch(beSecondary, perLegExplanation?.secondary?.evidence),
        });
      }
    } else {
      nodesForRender.forEach((node, index) => {
        if (node.kind !== 'signal') return;
        const criteria = node.extraInfo || [];
        if (criteria.length === 0) return;
        metCriteriaGroups.push({
          from: nodesForRender[index - 1]?.avatarName || '',
          to: nodesForRender[index + 1]?.avatarName || '',
          criteria: criteriaWithLastTouch(criteria),
        });
      });
    }
    const metCriteria = metCriteriaGroups.flatMap((group) => group.criteria.map((c) => c.label));
    const strength = {
      level: LEVELS.includes(level) ? level : 'unknown',
      metCriteria,
      metCriteriaGroups,
      legLevels,
    };

    return {
      nodesForRender,
      strength,
    };
  }, [currentUserId, pathNodes, level, perLegStrength, perLegCriteria, perLegExplanation]);
}
